import { memo } from 'react';

/**
 * AudioCard — audio "receipt" thumbnail for the inventory grid.
 *
 * Renders structured intel from the extraction pipeline instead of a
 * generic music-note placeholder. The grid currently picks between
 * <img src={preview}> and an icon span; this is a third branch for
 * docs where the music-note tells the user nothing.
 *
 * Reads from doc.extraction.audio_card (server-trimmed bundle of
 * speakers + first chapter + entities + duration_ms) and the doc's
 * classification fields. Fully composed client-side.
 */

const SPEAKER_PALETTE = [
  '#3b82f6', // blue
  '#ef4444', // red
  '#10b981', // green
  '#f59e0b', // amber
];

function formatDuration(ms) {
  if (!ms || !Number.isFinite(ms)) return null;
  const total = Math.round(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function typeBadge(doc) {
  const purpose = doc.document_purpose_group || doc.document_purpose;
  if (purpose && typeof purpose === 'string') return purpose;
  const type = doc.document_type;
  if (type && typeof type === 'string') return type.replace(/_/g, ' ');
  return 'Audio';
}

function SpeakerBand({ speakers }) {
  if (!Array.isArray(speakers) || speakers.length === 0) return null;
  const total = speakers.reduce((acc, s) => acc + (s.segments || 0), 0);
  if (total <= 0) return null;
  return (
    <div className="inv-audio-band" aria-hidden="true">
      {speakers.map((s, idx) => {
        const pct = ((s.segments || 0) / total) * 100;
        if (pct <= 0) return null;
        return (
          <span
            key={s.label || idx}
            className="inv-audio-band-bar"
            style={{
              width: `${pct}%`,
              background: SPEAKER_PALETTE[idx % SPEAKER_PALETTE.length],
            }}
            title={`${s.label}: ${s.segments} segments`}
          />
        );
      })}
    </div>
  );
}

function AudioCard({ doc }) {
  const card = doc.extraction?.audio_card;
  // card.duration_ms is the actual media length; extraction.duration_ms
  // is processing wall-clock time, not media duration.
  const duration = formatDuration(card?.duration_ms);
  const badge = typeBadge(doc);
  const speakers = card?.speakers || [];
  const chapter = card?.chapter;
  const entities = card?.entities || [];

  // Two-speaker shorthand for jail calls / interrogations: "Person A ⇄ Person B"
  let speakerLine = null;
  if (entities.length >= 2) {
    speakerLine = `${entities[0].text} ⇄ ${entities[1].text}`;
  } else if (entities.length === 1) {
    speakerLine = entities[0].text;
  } else if (speakers.length >= 2) {
    speakerLine = `${speakers[0].label} ⇄ ${speakers[1].label}`;
  } else if (speakers.length === 1) {
    speakerLine = speakers[0].label;
  }

  const gist = chapter?.gist || chapter?.headline || null;

  return (
    <div className="inv-audio-card">
      <div className="inv-audio-top">
        <span className="inv-audio-badge">{badge}</span>
        {duration && <span className="inv-audio-duration">{duration}</span>}
      </div>
      <div className="inv-audio-body">
        {speakerLine && (
          <div className="inv-audio-speakers" title={speakerLine}>
            {speakerLine}
          </div>
        )}
        {gist && (
          <div className="inv-audio-gist" title={gist}>
            {gist}
          </div>
        )}
        {!speakerLine && !gist && (
          <div className="inv-audio-empty">Audio recording</div>
        )}
      </div>
      <SpeakerBand speakers={speakers} />
    </div>
  );
}

export default memo(AudioCard);
